import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderMatchGroupResultsTooltip,
  renderMatchResultCell,
  renderMatchResultRowHeader,
  renderStatusCell,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Pii from 'dashboard/components/Pii';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  CheckName,
  CheckStatus,
  DatabaseBusinessAiIdentityComparison,
  DatabaseBusinessIdentityComparison,
  Match,
} from 'dashboard/models/check';
import { CheckResult } from 'dashboard/models/check-result/check-result';
import { VerificationDatabaseBusiness } from 'dashboard/models/verification';
import { COUNTRIES } from 'lib/constants/locales';
import { sortChecks } from 'lib/utils/checks';

export const computeDatabaseBusinessAttributeChecksMetadata = (
  verification: VerificationDatabaseBusiness,
): CheckMetadata => {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.DatabaseBusinessIdentityComparison,
  );

  const aiIdentityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.DatabaseBusinessAiIdentityComparison,
  );

  if (identityComparisonCheck && identityComparisonCheck.status === CheckStatus.Passed) {
    return computeMetadataForIdentityComparisonCheck(
      identityComparisonCheck as DatabaseBusinessIdentityComparison,
      verification,
      isRedacted,
    );
  } else if (aiIdentityComparisonCheck && aiIdentityComparisonCheck.status === CheckStatus.Passed) {
    return computeMetadataForAiIdentityComparisonCheck(
      aiIdentityComparisonCheck as DatabaseBusinessAiIdentityComparison,
      verification,
      isRedacted,
    );
  } else if (identityComparisonCheck) {
    return computeMetadataForIdentityComparisonCheck(
      identityComparisonCheck as DatabaseBusinessIdentityComparison,
      verification,
      isRedacted,
    );
  } else if (aiIdentityComparisonCheck) {
    return computeMetadataForAiIdentityComparisonCheck(
      aiIdentityComparisonCheck,
      verification,
      isRedacted,
    );
  }

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Identity Comparison Check',
        },
        rowHeaders: ['Property', 'Input', 'Match Result', 'Required'],
        rows: [],
        tableEmptyState: 'No comparison checks were performed for this verification.',
      },
    ] as CheckMetadataItem[],
  };
};

function computeMetadataForIdentityComparisonCheck(
  identityComparisonCheck: DatabaseBusinessIdentityComparison,
  verification: VerificationDatabaseBusiness,
  isRedacted: boolean,
): CheckMetadata {
  let recordsFound = false;
  if (identityComparisonCheck.status === CheckStatus.Passed) {
    recordsFound = true;
  } else if (
    identityComparisonCheck.status === CheckStatus.Failed &&
    identityComparisonCheck.reasons
  ) {
    recordsFound = !identityComparisonCheck.reasons.includes('no_records_found');
  }

  const checkResults = identityComparisonCheck?.metadata?.results ?? [];

  const comparisonMethods = checkResults
    .flatMap((result) => result['attribute-comparison-results'])
    .map((comparisonResult) => comparisonResult['comparison-type'])
    .filter((value, index, self) => self.indexOf(value) === index);
  const matchGroupResults = verification.attributes['match-group-results'];
  const addressCountryCode = verification.attributes['address-country-code'];

  let rows: (React.ReactNode | string)[][] = [[]];
  if (recordsFound) {
    rows = checkResults.map((result: CheckResult) => {
      const {
        properties,
        'normalized-claimed-attributes': normalizedClaimedAttributes,
        'normalized-extracted-attributes': normalizedExtractedAttributes,
        'attribute-comparison-results': attributeComparisonResults,
        'best-result': bestResult,
      } = result;

      const matchRequirementName = properties[0].replace(/_/g, ' ');

      return [
        titleCase(matchRequirementName),
        renderPropertyCell(normalizedClaimedAttributes[0], isRedacted),
        renderPropertyCell(normalizedExtractedAttributes[0], isRedacted),
        renderMatchResultCell(bestResult, attributeComparisonResults),
        renderStatusCell(result.status),
      ];
    });
  }

  const subheading = (
    <div>
      <div>Matching Method: Country-Specific Match Groups</div>
      {recordsFound && (
        <div>
          {identityComparisonCheck.status === CheckStatus.Passed ? 'Passed' : 'Best'} Match Group:{' '}
          {identityComparisonCheck?.metadata?.description}
          {matchGroupResults &&
            matchGroupResults.length > 0 &&
            renderMatchGroupResultsTooltip(
              matchGroupResults,
              `Match Groups (${COUNTRIES[addressCountryCode as keyof typeof COUNTRIES]})`,
            )}
        </div>
      )}
    </div>
  );

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Identity Comparison Check',
        },
        subheading,
        rowHeaders: [
          'Property',
          'Input',
          'Record',
          renderMatchResultRowHeader(comparisonMethods),
          'Status',
        ],
        rows,
        tableEmptyState: 'No records were found with the provided input.',
      },
    ] as CheckMetadataItem[],
  };
}

function computeMetadataForAiIdentityComparisonCheck(
  aiIdentityComparisonCheck: DatabaseBusinessAiIdentityComparison,
  verification: VerificationDatabaseBusiness,
  isRedacted: boolean,
): CheckMetadata {
  const {
    'business-name': businessName,
    'registration-number': registrationNumber,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'address-country-code': addressCountryCode,
    'match-group-results': matchGroupResults,
  } = verification.attributes;

  const addressFull = [
    addressStreet1,
    addressStreet2,
    addressCity,
    addressSubdivision,
    addressPostalCode,
    addressCountryCode,
  ]
    .filter(Boolean)
    .join(' ');

  const results = aiIdentityComparisonCheck.metadata?.results;

  // If the Check passed, we show columns for Property, Input, Record, Match Result, and Analysis.
  // We also show the Matching Method as AI and a tooltip for the Non-AI Match Groups Evaluated.
  if (aiIdentityComparisonCheck.status === CheckStatus.Passed) {
    const rows = [
      [
        'Business Name',
        renderPropertyCell(businessName || '', isRedacted),
        renderPropertyCell(results?.['matched-business-name'] || '', isRedacted),
        // TODO: fix typing for DatabaseBusinessAiIdentityComparison to remove the type coercion
        renderMatchResultCell(results?.['business-name-match-strength'] as Match),
        renderPropertyCell(results?.['business-name-analysis'] || '', isRedacted),
      ],
      [
        'Registration Number',
        renderPropertyCell(registrationNumber || '', isRedacted),
        renderPropertyCell(results?.['matched-registration-number'] || '', isRedacted),
        // TODO: fix typing for DatabaseBusinessAiIdentityComparison to remove the type coercion
        renderMatchResultCell(results?.['registration-number-match-strength'] as Match),
        renderPropertyCell(results?.['registration-number-analysis'] || '', isRedacted),
      ],
      [
        'Address',
        renderPropertyCell(addressFull || '', isRedacted),
        renderPropertyCell(results?.['matched-address'] || '', isRedacted),
        // TODO: fix typing for DatabaseBusinessAiIdentityComparison to remove the type coercion
        renderMatchResultCell(results?.['address-match-strength'] as Match),
        renderPropertyCell(results?.['address-analysis'] || '', isRedacted),
      ],
    ];

    const subheading = (
      <div>
        Matching Method: AI Match Groups
        {matchGroupResults &&
          matchGroupResults.length > 0 &&
          renderMatchGroupResultsTooltip(matchGroupResults, 'Non-AI Match Groups Evaluated')}
      </div>
    );

    return {
      metadata: [
        {
          type: CheckMetadataItemType.Table,
          heading: {
            title: 'Identity Comparison Check',
          },
          subheading,
          rowHeaders: ['Property', 'Input', 'Record', 'Match Result', 'Analysis'],
          rows,
          tableEmptyState: 'No business matches were found using AI comparison.',
        },
      ] as CheckMetadataItem[],
    };
  }

  // If the Check did not pass, we only show columns for Property, Input, and Match Result.
  // We also show the Matching Method as both Country-Specific & AI.
  else {
    const rows = [
      [
        'Business Name',
        renderPropertyCell(businessName || '', isRedacted),
        // TODO: fix typing for DatabaseBusinessAiIdentityComparison to remove the type coercion
        renderMatchResultCell(results?.['business-name-match-strength'] as Match),
      ],
      [
        'Registration Number',
        renderPropertyCell(registrationNumber || '', isRedacted),
        // TODO: fix typing for DatabaseBusinessAiIdentityComparison to remove the type coercion
        renderMatchResultCell(results?.['registration-number-match-strength'] as Match),
      ],
      [
        'Address',
        renderPropertyCell(addressFull || '', isRedacted),
        // TODO: fix typing for DatabaseBusinessAiIdentityComparison to remove the type coercion
        renderMatchResultCell(results?.['address-match-strength'] as Match),
      ],
    ];

    return {
      metadata: [
        {
          type: CheckMetadataItemType.Table,
          heading: {
            title: 'Identity Comparison Check',
          },
          subheading: 'Matching Methods: Country-Specific & AI Match Groups',
          rowHeaders: ['Property', 'Input', 'Match Result'],
          rows,
          tableEmptyState: 'No business matches were found using AI comparison.',
        },
      ] as CheckMetadataItem[],
    };
  }
}

function renderPropertyCell(value: string, isRedacted: boolean) {
  return <Pii isRedacted={isRedacted}>{value}</Pii>;
}

export function getStatusForCheckRow(verification: VerificationDatabaseBusiness) {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.DatabaseBusinessIdentityComparison,
  );

  const aiIdentityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.DatabaseBusinessAiIdentityComparison,
  );

  if (identityComparisonCheck && identityComparisonCheck.status === CheckStatus.Passed) {
    return identityComparisonCheck.status;
  } else if (aiIdentityComparisonCheck && aiIdentityComparisonCheck.status === CheckStatus.Passed) {
    return aiIdentityComparisonCheck.status;
  } else if (identityComparisonCheck) {
    return identityComparisonCheck.status;
  } else if (aiIdentityComparisonCheck) {
    return aiIdentityComparisonCheck.status;
  }
}
